import { Design } from 'blaze-ui/icons'
import { DesignChannel } from 'components/NewFileModal/Types'
import { array, boolean, number, object, string } from 'yup'
import { safify } from '../utils/safify'
import { StepSchema } from '../Types'
import { sourceVisualElementId } from '../shared/sourceVisualElementId'
import { caption } from '../shared/caption'
import { designContent } from '../shared/designContent'
import { filename } from '../shared/filename'
import { designProject } from '../shared/designProject'
import { crossPostAccounts } from '../shared/crossPostAccounts'
import { captionType } from '../shared/captionType'
import { designTopic } from '../shared/designTopic'
import { brandKitId } from '../shared/brandKitId'
import { brandVoiceId, tone } from '../shared/voiceTones'
import { sourceMaterialIds } from '../shared/sourceMaterials'
import { includeEmojis } from '../shared/includeEmojis'
import { audience } from '../shared/audience'
import { postLength } from '../shared/postLength'
import { PostLength } from 'components/Wizards/ContentPlanning/utils'
import { links } from '../shared/links'
import { avoid } from '../shared/avoid'
import { language } from '../shared/language'

export const meta = {
  channel: DesignChannel.Post,
  title: 'Design Wizard',
  previewUrl: '',
  icon: Design,
}

export const SelectMediaBaseSchema = object({
  initial_media_file_id: number().default(undefined).transform(safify),
  selected_images: array().of(string()).default([]).transform(safify),
  featured_selected_images: array()
    .of(
      object({
        url: string().required(),
        alt_text: string().default(''),
      }),
    )
    .default([])
    .transform(safify),
}) as StepSchema

export const ImageSelectionSchema = SelectMediaBaseSchema.meta({
  ...SelectMediaBaseSchema.describe().meta,
  title: 'Select Image',
  header: 'Start with an image',
  supportedMediaTypes: ['image'],
  previousLabel: 'Cancel',
  submitLabel: 'Confirm Selected Image',
}) as StepSchema

export const ImageAndBackgroundSchema = object({
  width: number().required(),
  height: number().required(),
  initial_media_file_remove_background: boolean().default(false).meta({ hidden: true }),
}).meta({
  title: 'Confirm image selection',
  submitLabel: 'Set Purpose & Goals',
  hideStepHeader: true,
}) as StepSchema

export const ImageAndBackgroundModalSchema = object({
  width: number().required(),
  height: number().required(),
  initial_media_file_remove_background: boolean().default(false).meta({ hidden: true }),
}).meta({
  title: 'Confirm image selection',
  previousLabel: 'Back',
  submitLabel: 'Add Image',
  hideStepHeader: true,
}) as StepSchema

export const TemplateSchema = object({
  source_visual_element_id: sourceVisualElementId,
  caption,
  content: designContent,
  filename: filename,
  project: designProject,
  cross_post_accounts: crossPostAccounts,
}).meta({
  title: 'Refine Details',
  header: 'Refine post details',
  submitLabel: 'Generate Post',
}) as StepSchema

export const ReviewSchema = object({}).meta({
  title: 'Post Preview',
  allowBack: false,
  allowClose: false,
  stepId: 'review',
}) as StepSchema

export const PurposeAndGoalsSchema = ({ generationType }: { generationType: string }) =>
  object({
    topic: designTopic
      .meta({
        label: 'What is your post about?',
        description: 'We used your Brand Kit to suggest this prompt',
        generate: true,
        writeMyOwn: true,
        generationType,
      })
      .required(),
    brand_kit_id: brandKitId,
    tone: tone.optional(),
    brand_voice_id: brandVoiceId,
    source_material_ids: sourceMaterialIds,
    language,
  }).meta({
    title: 'Set Purpose',
    header: 'Set purpose and goals',
    submitLabel: 'Refine Post Details',
  }) as StepSchema

export const CaptionDetailsSchema = ({ generationType }: { generationType: string }) =>
  object({
    caption_type: captionType,
    topic: designTopic
      .meta({
        label: "What's the topic of your post?",
        description: 'We used your Brand Kit to suggest this prompt',
        generate: true,
        writeMyOwn: true,
        generationType,
      })
      .required(),
    brand_kit_id: brandKitId,
    tone: tone.optional(),
    brand_voice_id: brandVoiceId,
    source_material_ids: sourceMaterialIds,
    filename: filename
      .meta({
        label: 'Name your doc and select a location',
      })
      .required(),
    project: designProject,
    include_emojis: includeEmojis.meta({ inCollapsibleSection: true }),
    audience: audience.meta({ label: 'Who is the target audience?', inCollapsibleSection: true }),
    post_length: postLength
      .meta({ inCollapsibleSection: true })
      .required()
      .oneOf([
        PostLength.FiveToTenWords,
        PostLength.TenToTwentyFiveWords,
        PostLength.TwentyFiveToFiftyWords,
        PostLength.FiftyToSeventyFiveWords,
        PostLength.SeventyFivetoOneHundredWords,
        PostLength.OneHudredPlusWords,
      ])
      .default(PostLength.TenToTwentyFiveWords),
    links: links.meta({ collapsible: true }),
    avoid: avoid.meta({ collapsible: true }),
    language,
  }).meta({
    title: 'Refine Details',
    previousLabel: 'Set Purpose',
    header: 'Refine caption details',
    submitLabel: 'Generate Post',
  }) as StepSchema
